import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { APIProvider, Map } from "@vis.gl/react-google-maps";
import { defineMessages, FormattedMessage } from "react-intl";
import { DeckGLOverlay } from "./DeckGLOverlay";
import { MetricsViewOverlay } from "./MetricsViewOverlay";
import { JunctionsViewOverlay } from "./JunctionsViewOverlay";
import type { Layer } from "@deck.gl/core";
import { MapMainProps } from "@/pages/index";
import { useInitialMapState } from "@/api/hooks/useInitialMapState";
import { googleMapsApiKey, googleMapsId, fallbackViewState } from "@/utils/constants";
import { useMetricsView } from "@/hooks/views/useMetricsView";
import { useJunctionAnalyticsView } from "@/hooks/views/useJunctionAnalyticsView";
import { ViewType } from "@/components/ui/Menu";
import { getMetricTypeForViewMode } from "@/utils/metricUtils";
const messages = defineMessages({
  mapLoading: {
    id: "map.loading",
    defaultMessage: "Loading map..."
  }
});
export const MapMain = t0 => {
  const $ = _c(44);
  const {
    displayedVehicleTypes,
    selectedMetric,
    viewMode,
    selectedStartTime,
    selectedEndTime,
    rangeValues,
    activeView
  } = t0;
  const {
    data,
    error,
    isLoading
  } = useInitialMapState();
  const isJunctionAnalytics = activeView === ViewType.junctionAnalytics;
  let t1;
  if ($[0] !== selectedMetric || $[1] !== viewMode) {
    t1 = getMetricTypeForViewMode(selectedMetric, viewMode);
    $[0] = selectedMetric;
    $[1] = viewMode;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = !isJunctionAnalytics;
  let t3;
  if ($[3] !== displayedVehicleTypes || $[4] !== rangeValues || $[5] !== selectedEndTime || $[6] !== selectedStartTime || $[7] !== t1 || $[8] !== t2) {
    t3 = {
      selectedMetric: t1,
      displayedVehicleTypes,
      selectedStartTime,
      selectedEndTime,
      rangeValues,
      visible: t2
    };
    $[3] = displayedVehicleTypes;
    $[4] = rangeValues;
    $[5] = selectedEndTime;
    $[6] = selectedStartTime;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const trajectoryLayers = useMetricsView(t3);
  let t4;
  if ($[10] !== displayedVehicleTypes || $[11] !== isJunctionAnalytics || $[12] !== selectedEndTime || $[13] !== selectedStartTime) {
    t4 = {
      active: isJunctionAnalytics,
      displayedVehicleTypes,
      selectedStartTime,
      selectedEndTime
    };
    $[10] = displayedVehicleTypes;
    $[11] = isJunctionAnalytics;
    $[12] = selectedEndTime;
    $[13] = selectedStartTime;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const {
    markersLayer,
    gatesLayer,
    segmentsLayer,
    selectedJunctionId,
    selectedMovementId,
    setSelectedMovementId,
    clearSelectedJunction,
    vehicleCountsData
  } = useJunctionAnalyticsView(t4);
  let t5;
  if ($[15] !== gatesLayer || $[16] !== markersLayer || $[17] !== segmentsLayer || $[18] !== trajectoryLayers) {
    t5 = [...trajectoryLayers, markersLayer, ...segmentsLayer, gatesLayer].filter(_temp);
    $[15] = gatesLayer;
    $[16] = markersLayer;
    $[17] = segmentsLayer;
    $[18] = trajectoryLayers;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const layers = t5 as Layer[];
  if (isLoading) {
    let t6;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <div><FormattedMessage {...messages.mapLoading} /></div>;
      $[20] = t6;
    } else {
      t6 = $[20];
    }
    return t6;
  }
  if (error) {
    console.error("Error fetching initial map state. Fallback to default view state.", error);
  }
  let t6;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      position: "absolute",
      overflow: "hidden"
    };
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const t7 = data?.latitude ?? fallbackViewState.latitude;
  const t8 = data?.longitude ?? fallbackViewState.longitude;
  let t9;
  if ($[22] !== t7 || $[23] !== t8) {
    t9 = {
      lat: t7,
      lng: t8
    };
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const t10 = data?.zoom ?? fallbackViewState.zoom;
  let t11;
  if ($[25] !== layers) {
    t11 = <DeckGLOverlay layers={layers} />;
    $[25] = layers;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t10 || $[28] !== t11 || $[29] !== t9) {
    t12 = <APIProvider apiKey={googleMapsApiKey}><Map style={t6} defaultCenter={t9} defaultZoom={t10} mapId={googleMapsId} fullscreenControl={false} streetViewControl={false} cameraControl={false}>{t11}</Map></APIProvider>;
    $[27] = t10;
    $[28] = t11;
    $[29] = t9;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== clearSelectedJunction || $[32] !== displayedVehicleTypes || $[33] !== isJunctionAnalytics || $[34] !== rangeValues || $[35] !== selectedJunctionId || $[36] !== selectedMetric || $[37] !== selectedMovementId || $[38] !== setSelectedMovementId || $[39] !== vehicleCountsData) {
    t13 = isJunctionAnalytics ? <JunctionsViewOverlay selectedJunctionId={selectedJunctionId} vehicleCountsData={vehicleCountsData} selectedMovementId={selectedMovementId} onSelectMovement={setSelectedMovementId} onClose={clearSelectedJunction} /> : <MetricsViewOverlay selectedMetric={selectedMetric} displayedVehicleTypes={displayedVehicleTypes} rangeValues={rangeValues} />;
    $[31] = clearSelectedJunction;
    $[32] = displayedVehicleTypes;
    $[33] = isJunctionAnalytics;
    $[34] = rangeValues;
    $[35] = selectedJunctionId;
    $[36] = selectedMetric;
    $[37] = selectedMovementId;
    $[38] = setSelectedMovementId;
    $[39] = vehicleCountsData;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] !== t12 || $[42] !== t13) {
    t14 = <>{t12}{t13}</>;
    $[41] = t12;
    $[42] = t13;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  return t14;
};
function _temp(layer) {
  return layer != null;
}